/**
 * Tasks notifications (ONE-4840 / ONE-4832).
 *
 * The API writes `sourceType: task_submission` (or still `form_submission`
 * with `form_type: task`) and these activity types:
 *   task_completed, task_comment_created, task_note_created,
 *   task_status_updated, task_available, task_instance_created
 */

export const ETaskNotificationActivities = {
  COMPLETED: 'task_completed',
  COMMENT_CREATED: 'task_comment_created',
  NOTE_CREATED: 'task_note_created',
  STATUS_UPDATED: 'task_status_updated',
  AVAILABLE: 'task_available',
  INSTANCE_CREATED: 'task_instance_created',
} as const;

export type TaskNotificationActivity =
  typeof ETaskNotificationActivities[keyof typeof ETaskNotificationActivities];

export function isTaskNotification(notification: {
  source_type?: string | null;
  activity_type?: string | null;
  meta?: Record<string, unknown> | null;
}): boolean {
  if (notification.source_type === 'task_submission') return true;
  if ((notification.activity_type ?? '').startsWith('task_')) return true;

  const meta = notification.meta ?? {};
  const form = meta.form as { form_type?: string } | undefined;
  const formType = form?.form_type ?? (meta.form_type as string | undefined);

  return formType === 'task';
}

function asId(value: unknown): string | undefined {
  if (typeof value === 'string' && value) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
}

export function getTaskNotificationFormId(notification: {
  source_id?: string | null;
  meta?: Record<string, unknown> | null;
}): string | undefined {
  const meta = notification.meta ?? {};
  const form = meta.form as { id?: unknown } | undefined;
  const task = meta.task as { id?: unknown } | undefined;

  return asId(form?.id)
    ?? asId(meta.form_id)
    ?? asId(meta.task_id)
    ?? asId(task?.id);
}

export function isTaskAvailableActivity(activityType?: string | null): boolean {
  return activityType === ETaskNotificationActivities.AVAILABLE
    || activityType === ETaskNotificationActivities.INSTANCE_CREATED;
}

export function isTaskCommentActivity(activityType?: string | null): boolean {
  return activityType === ETaskNotificationActivities.COMMENT_CREATED
    || activityType === ETaskNotificationActivities.NOTE_CREATED;
}

export function getTaskNotificationPath(options: {
  formId?: string | null;
  submissionId?: string | null;
  isAdmin?: boolean;
  isOwn?: boolean;
  showComments?: boolean;
}): string | null {
  const {
    formId, submissionId, isAdmin = false, isOwn = false, showComments = false,
  } = options;
  if (!formId) return null;

  if (isOwn) {
    if (!submissionId) return `/tasks/${formId}/progress`;
    return `/tasks/${formId}/progress/${submissionId}`;
  }

  const base = isAdmin ? '/admin/tasks' : '/tasks';
  if (!submissionId) return `${base}/${formId}/edit/submissions`;
  const comments = showComments ? '?showComments=true' : '';
  return `${base}/${formId}/edit/submissions/${submissionId}${comments}`;
}
